import templateCatalog from './recommendationConfig/templateCatalog.json'
import {
    DESCRIPTION_RECOMMENDATION_SIGNALS,
    RECOMMENDATION_CONFIG,
    TILE_RECOMMENDATION_SIGNALS,
} from './recommendationConfig/signals'

const START_FROM_SCRATCH_NAME = 'start from scratch'
const WORD_MIN_LENGTH = 3

const normalizeText = value =>
    String(value || '')
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

const getWords = value =>
    normalizeText(value)
        .split(/[^\p{L}\p{N}]+/u)
        .filter(word => word.length >= WORD_MIN_LENGTH)

const normalizeLanguage = language => normalizeText(language).split(/[-_]/)[0]

const normalizeTemplateName = name =>
    normalizeText(name)
        .replace(/\([^)]*\)/g, '')
        .replace(/\b(spanish|english|french|german|italian|portuguese|korean|russian)\b/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()

const countMatches = (templateTags, signalTags = []) =>
    signalTags.reduce((count, tag) => count + (templateTags.has(tag) ? 1 : 0), 0)

const getDescriptionSignals = description => {
    const normalizedDescription = normalizeText(description)
    return DESCRIPTION_RECOMMENDATION_SIGNALS.filter(signal =>
        signal.phrases.some(phrase => normalizedDescription.includes(normalizeText(phrase))),
    )
}

const getLanguageScore = (templateLanguage, preferredLanguage) => {
    const normalizedTemplateLanguage = normalizeLanguage(templateLanguage)
    const normalizedPreferredLanguage = normalizeLanguage(preferredLanguage)

    if (normalizedPreferredLanguage && normalizedTemplateLanguage === normalizedPreferredLanguage) {
        return RECOMMENDATION_CONFIG.preferredLanguageBonus
    }
    if (normalizedTemplateLanguage === 'any') return RECOMMENDATION_CONFIG.languageNeutralBonus
    if (normalizedTemplateLanguage === 'en') return RECOMMENDATION_CONFIG.englishFallbackBonus
    return 0
}

const getPopularityScore = (template, catalogSize) => {
    const useCount = Number(template.useCount) || 0
    const rank = Number(template.rank) || catalogSize
    const usageBonus = Math.min(Math.log1p(useCount) / 10, RECOMMENDATION_CONFIG.maxPopularityBonus)
    const rankBonus = Math.max(0, (catalogSize - rank) / catalogSize) * 0.25
    return usageBonus + rankBonus
}

const scoreTemplate = ({ template, selectedTileIds, description, preferredLanguage, catalogSize }) => {
    const templateTags = new Set(template.tags || [])
    const matchedTileIds = []
    const matchedMechanicIds = []
    let score = 0

    selectedTileIds.forEach(tileId => {
        const signal = TILE_RECOMMENDATION_SIGNALS[tileId]
        if (!signal) return

        const tagMatchCount = countMatches(templateTags, signal.tags)
        if (tagMatchCount > 0) {
            score += signal.weight * (1 + (tagMatchCount - 1) * RECOMMENDATION_CONFIG.extraTagMatchFactor)
            matchedTileIds.push(tileId)
            if (signal.diversityGroup) matchedMechanicIds.push(tileId)
        }

        if (signal.preferredMode && template.mode === signal.preferredMode) {
            score += RECOMMENDATION_CONFIG.preferredModeBonus
        }

        if (countMatches(templateTags, signal.excludedTags) > 0) {
            score -= RECOMMENDATION_CONFIG.excludedTagPenalty
        }
    })

    const descriptionSignals = getDescriptionSignals(description)
    const matchedDescriptionSignals = descriptionSignals.filter(signal => countMatches(templateTags, signal.tags) > 0)
    score += matchedDescriptionSignals.length * RECOMMENDATION_CONFIG.descriptionSignalWeight

    const descriptionWords = new Set(getWords(description))
    const matchingNameWords = new Set(getWords(template.name).filter(word => descriptionWords.has(word)))
    score += Math.min(
        matchingNameWords.size * RECOMMENDATION_CONFIG.descriptionNameTokenWeight,
        RECOMMENDATION_CONFIG.maxDescriptionNameTokenScore,
    )

    score += getLanguageScore(template.language, preferredLanguage)
    if (templateTags.has('most_popular')) score += RECOMMENDATION_CONFIG.popularTagBonus
    score += getPopularityScore(template, catalogSize)

    return {
        template,
        score,
        matchedTileIds,
        matchedMechanicIds,
        descriptionMatched: matchedDescriptionSignals.length > 0 || matchingNameWords.size > 0,
        languageMatched:
            Boolean(normalizeLanguage(preferredLanguage)) &&
            normalizeLanguage(template.language) === normalizeLanguage(preferredLanguage),
    }
}

const compareCandidates = (firstCandidate, secondCandidate) => {
    if (secondCandidate.score !== firstCandidate.score) return secondCandidate.score - firstCandidate.score
    return (
        (Number(firstCandidate.template.rank) || Number.MAX_SAFE_INTEGER) -
        (Number(secondCandidate.template.rank) || Number.MAX_SAFE_INTEGER)
    )
}

const isUsableTemplate = template =>
    template &&
    template.id &&
    template.name &&
    template.url &&
    normalizeTemplateName(template.name) !== START_FROM_SCRATCH_NAME

const selectCandidate = (selectedCandidates, candidates, predicate = () => true) => {
    const selectedNames = new Set(selectedCandidates.map(candidate => normalizeTemplateName(candidate.template.name)))
    return candidates.find(
        candidate =>
            !selectedCandidates.some(selected => selected.template.id === candidate.template.id) &&
            !selectedNames.has(normalizeTemplateName(candidate.template.name)) &&
            predicate(candidate),
    )
}

const selectDiverseCandidates = (candidates, selectedTileIds, resultCount) => {
    const selectedCandidates = []
    const selectedMechanicIds = selectedTileIds.filter(
        tileId => TILE_RECOMMENDATION_SIGNALS[tileId] && TILE_RECOMMENDATION_SIGNALS[tileId].diversityGroup,
    )

    selectedMechanicIds.slice(0, resultCount).forEach(mechanicId => {
        const candidate = selectCandidate(selectedCandidates, candidates, item =>
            item.matchedMechanicIds.includes(mechanicId),
        )
        if (candidate) selectedCandidates.push(candidate)
    })

    while (selectedCandidates.length < resultCount) {
        const candidate =
            selectCandidate(
                selectedCandidates,
                candidates,
                item => selectedMechanicIds.length === 0 || item.matchedMechanicIds.length > 0,
            ) || selectCandidate(selectedCandidates, candidates)
        if (!candidate) break
        selectedCandidates.push(candidate)
    }

    return selectedCandidates
}

const ensureFreeTemplate = (selectedCandidates, candidates, hasSelectedMechanic) => {
    if (selectedCandidates.some(candidate => candidate.template.isPremium === false)) return selectedCandidates

    const freeCandidate =
        selectCandidate(
            selectedCandidates,
            candidates,
            candidate =>
                candidate.template.isPremium === false &&
                (!hasSelectedMechanic || candidate.matchedMechanicIds.length > 0),
        ) || selectCandidate(selectedCandidates, candidates, candidate => candidate.template.isPremium === false)
    if (!freeCandidate) return selectedCandidates

    return [...selectedCandidates.slice(0, -1), freeCandidate]
}

/**
 * Deterministic, explainable template ranking. The catalog argument is exposed
 * for tests and future catalog versions; production uses the checked-in file.
 */
export const rankEventTemplates = ({
    catalog = templateCatalog,
    selectedTileIds = [],
    description = '',
    preferredLanguage = 'en',
    limit = RECOMMENDATION_CONFIG.resultCount,
    excludedTemplateIds = [],
}) => {
    const catalogSize = catalog.length
    const excludedIds = new Set(excludedTemplateIds.map(String))
    const excludedNames = new Set(
        catalog
            .filter(template => excludedIds.has(String(template.id)))
            .map(template => normalizeTemplateName(template.name)),
    )
    const candidates = catalog
        .filter(
            template =>
                isUsableTemplate(template) &&
                !excludedIds.has(String(template.id)) &&
                !excludedNames.has(normalizeTemplateName(template.name)),
        )
        .map(template => scoreTemplate({ template, selectedTileIds, description, preferredLanguage, catalogSize }))
        .sort(compareCandidates)
    const hasSelectedMechanic = selectedTileIds.some(
        tileId => TILE_RECOMMENDATION_SIGNALS[tileId] && TILE_RECOMMENDATION_SIGNALS[tileId].diversityGroup,
    )

    return ensureFreeTemplate(
        selectDiverseCandidates(candidates, selectedTileIds, limit),
        candidates,
        hasSelectedMechanic,
    ).map(candidate => ({
        template: candidate.template,
        matchedTileIds: candidate.matchedTileIds
            .slice()
            .sort(
                (firstTileId, secondTileId) =>
                    TILE_RECOMMENDATION_SIGNALS[secondTileId].weight - TILE_RECOMMENDATION_SIGNALS[firstTileId].weight,
            )
            .slice(0, 3),
        descriptionMatched: candidate.descriptionMatched,
        languageMatched: candidate.languageMatched,
    }))
}

export const recommendEventTemplates = (options = {}) => rankEventTemplates({ ...options, catalog: templateCatalog })

/**
 * Async boundary for the recommendation source. The current implementation
 * uses the local catalog, but callers do not need to change when it becomes an
 * API request.
 */
export const loadEventTemplateRecommendations = async (options = {}) => recommendEventTemplates(options)
